import debounce from 'lodash/debounce';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getEnvLocaleData } from '../../api/api-env';
import { fetchMapsData } from './fetch-maps-data';

export const useLocationInput = (props) => {
  const {
    value,
    setValue,
    defaultLocation,
    setValidator,
    setActiveInput,
    activeInput,
    currentLocale,
    noHighlight
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const [defaultOptions, setDefaultOptions] = useState([]);
  const [isOptionError, setIsOptionError] = useState(false);
  const [inputDisplay, setInputDisplay] = useState(value?.label || '');
  const [, setInterimOption] = useState('');
  const [interimValue, setInterimValue] = useState('');
  const [dropdownChoice, setDropdownChoice] = useState();

  const isMounted = useRef(false);
  // this is a debounced function that fetches the location suggestions
  // the ref is used to avoid creating a new function on every render
  const _fetchSuggestions = useRef(debounce(async (inputValue, cb) => {
    if (0 > inputValue.length) return cb([]);

    try {
      const mapSuggestions = await fetchMapsData(inputValue);
      setDefaultOptions(mapSuggestions);
      setInterimValue(mapSuggestions[0]);
      setIsOptionError(false);
      return cb(mapSuggestions);
    } catch (e) {
      setIsOptionError(true);
      setInterimOption('');
    }
  }, 400));

  // useCallback is used to avoid creating a new function on every render
  const fetchSuggestions = useCallback((inputValue, cb) => {
    _fetchSuggestions.current(inputValue, cb);
  }, []);

  useEffect(() => {
    const trojanElement = document.getElementById('initial-data') || null;
    if (defaultLocation) {
      onChange(defaultLocation, { action: 'select-option' }, false);
    } else if (!defaultLocation && trojanElement && trojanElement.hasAttribute('data-env')) {
      // if we have a trojanElement we want to avoid an api call and pull the location prefill from that
      const envData = JSON.parse(trojanElement.getAttribute('data-env'));
      envData.location_prefill && onChange(envData.location_prefill, { action: 'select-option' }, false);
    } else {
      getEnvLocaleData(currentLocale?.locale)
        .then((envObj) => {
          const suggestedLocation = envObj.location_prefill;
          if (suggestedLocation) {
            // initiateMapFetch(suggestedLocation);
            onChange(suggestedLocation, { action: 'select-option' }, false);
          }
        })
        .catch((e) => console.error(e));
    }
  }, []);

  const validate = useCallback(() => {
    let isValid = true;
    const hasValue = !(
      isEmpty(value) ||
      isEmpty(value.query) ||
      !value.query.location
    );

    if (!hasValue) {
      isValid = false;
      focus();
    }
    return isValid;
  }, [value]);

  useEffect(() => {
    if ('function' === typeof setValidator) {
      setValidator(validate);
    }
  }, [setValidator, validate]);

  useEffect(() => {
    const displayValue = 'object' === typeof(value) ? get(value, 'label', '') : value;
    setInputDisplay(displayValue);
  }, [value]);

  useEffect(() => {
    // check for changes in the dropdown selections
    if (!isEmpty(dropdownChoice)) {
      setValue(dropdownChoice);
    }
  }, [dropdownChoice]);

  useEffect(() => {
    // due to async nature of fetching google maps,
    // when there isn't a value sometimes we still see the dropdown
    // especially when someone deletes the input very quickly. Therefore,
    // we should reset the dropdown options if there isn't a value in the input
    if (!isEmpty(defaultOptions) && (isEmpty(value) && undefined !== value)) {
      setDefaultOptions([]);
      setDropdownChoice({});
    }
  }, [defaultOptions]);

  const onChange = (onChangeValue, { action }, shouldBlur = true, callback) => {
    if (!value && 'clear' === action) {
      setDefaultOptions([]);
      setDropdownChoice({});
      return;
    }
    const placeId = get(onChangeValue, 'value.place_id');
    // we want to keep track of the user's interaction from the dropdown in order to
    // distinguish whether a choice was automatically selected because the user filled out
    // an incomplete string or if the user selected a choice from the dropdown
    if (!dropdownChoice) {
      // on initial load the user has not made a choice from the dropdown
      // therefore value is set immediately
      setValue(onChangeValue);
    }
    setDropdownChoice(onChangeValue);
    if (placeId && 'undefined' !== typeof window) {
      window.PSSessions.Cookies.set('place_id', placeId, {
        path: '/',
        expires: new Date(Date.now() + (60 * 60 * 6 * 1000)),
        domain: window.location.host,
        secure: -1 !== window.location.protocol.indexOf('s')
      });
    }
    if (callback) {
      return callback(action);
    }
  };

  const inputChange = (value, { action }) => {
    if (!value && 'input-change' === action) {
      setDefaultOptions([]);
      setValue({});
    }
    if ('input-change' === action) {
      setInputDisplay(value || '');
      setDropdownChoice({});
    }
  };

  const handleInputFocus = async (e) => {
    if (e) {
      e.preventDefault();
      if (!noHighlight) {
        e.target.setSelectionRange(0, e.target.value.length);
      }
    }
    // this ensures that if there is a value in the input
    // we should populate the dropdown options when the user focuses
    if (isEmpty(defaultOptions) && !isEmpty(value)) {
      try {
        const mapSuggestions = await fetchMapsData(inputDisplay);
        setDefaultOptions(mapSuggestions);
      } catch (e) {
        setIsOptionError(true);
      }
    }
    setIsOptionError(false);
    setIsFocused(true);
    if ('function' === typeof setActiveInput) {
      setActiveInput('location');
    }
  };

  const blur = (selected = false) => {
    setIsFocused(false);
    if ('function' === typeof setActiveInput) {
      setActiveInput('');
    }
  };

  const handleInputBlur = (e) => {
    e.preventDefault();
    // if the user has not selected from the dropdown and blurs out of
    // the input field, we can assume that the value is an incomplete string,
    // therefore we will automatically set the interim value as the fist choice
    // in the dropdown.
    if (inputDisplay) {
      if (isEmpty(dropdownChoice) && interimValue) {
        setValue(interimValue, undefined, true);
      }
      if (!isEmpty(dropdownChoice)) {
        setValue(dropdownChoice);
      }
    }
    blur();
  };

  const handleContainerFocus = () => {
    if ('function' === typeof setActiveInput) {
      setActiveInput('location');
    }
    setIsFocused(true);
  };

  const focus = () => {
    handleContainerFocus();
    handleInputFocus();
  };

  useEffect(() => {
    // activeInput is sometimes set by SearchForm
    if ('location' === activeInput && !isFocused) {
      focus();
    }
  }, [activeInput]);

  useEffect(() => {
    // blur on unmount
    return blur;
  }, []);

  useEffect(() => {
    isMounted.current = true;
    return () => (isMounted.current = false);
  }, []);

  return {
    fetchSuggestions,
    onChange,
    inputChange,
    handleInputFocus,
    handleInputBlur,
    handleContainerFocus,
    focus,
    blur,
    validate,
    defaultOptions,
    isOptionError,
    isFocused,
    inputDisplay,
    ...props
  };
};
